import React from 'react';
import { useIntl } from 'react-intl';
import type {
    ConsumptionModeRatesDto,
    OperationZoneDto,
    VatGroupWithRatesDto,
} from '@/services/innovorder/vatConfiguration/vatConfiguration.type';
import type { ConsumptionMode } from '../../VatRatesMatrix.viewmodel';
import { COUNTRY_FLAG_MAP } from '../../VatRatesMatrix.viewmodel';
import { getVatGroupLabel } from '../../utils';
import {
    EmptyCell,
    GroupCell,
    GroupCode,
    GroupName,
    HeaderCell,
    HeaderZoneName,
    RateCell,
    RateIcon,
    RateLabel,
    RateLine,
    RateValue,
    StyledTable,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
} from './MatrixTable.style';

interface MatrixTableProps {
    operationZones: OperationZoneDto[];
    vatGroups: VatGroupWithRatesDto[];
    modeFilter: ConsumptionMode | '';
}

const getRateColor = (basisPoints: number): string => {
    const percent = basisPoints / 100;
    if (percent <= 5.5) return '#2e7d32';
    if (percent <= 10) return '#e65100';
    return '#c62828';
};

const formatRate = (basisPoints: number): string => `${(basisPoints / 100).toFixed(1)}%`;

interface RateLineConfig {
    mode: ConsumptionMode;
    icon: string;
    labelId: string;
}

const RATE_LINES: RateLineConfig[] = [
    { mode: 'eatIn', icon: '\u{1F37D}\u{FE0F}', labelId: 'vatRatesMatrix.eatIn' },
    { mode: 'takeAway', icon: '\u{1F4E6}', labelId: 'vatRatesMatrix.takeAway' },
    { mode: 'delivery', icon: '\u{1F69A}', labelId: 'vatRatesMatrix.delivery' },
];

export const MatrixTable: React.FunctionComponent<MatrixTableProps> = ({ operationZones, vatGroups, modeFilter }) => {
    const intl = useIntl();

    const visibleRateLines = modeFilter ? RATE_LINES.filter((r) => r.mode === modeFilter) : RATE_LINES;

    return (
        <TableContainer>
            <StyledTable>
                <TableHead>
                    <tr>
                        <HeaderCell>{intl.formatMessage({ id: 'vatRatesMatrix.vatGroup' })}</HeaderCell>
                        {operationZones.map((zone) => (
                            <HeaderCell key={zone.code}>
                                <HeaderZoneName>
                                    {COUNTRY_FLAG_MAP[zone.countryCode] ?? '\u{1F3F3}\u{FE0F}'} {zone.name}
                                </HeaderZoneName>
                            </HeaderCell>
                        ))}
                    </tr>
                </TableHead>
                <TableBody>
                    {vatGroups.map((group) => (
                        <TableRow key={group.vatGroupId}>
                            <GroupCell>
                                <GroupName>{getVatGroupLabel(intl, group)}</GroupName>
                                <GroupCode>
                                    {intl.formatMessage({ id: 'vatRatesMatrix.codeLabel' })}: {group.code}
                                </GroupCode>
                            </GroupCell>
                            {operationZones.map((zone) => {
                                const rates = group.rates[zone.code];
                                if (!rates) {
                                    return <EmptyCell key={zone.code}>-</EmptyCell>;
                                }
                                return (
                                    <RateCell key={zone.code}>
                                        {visibleRateLines.map((line) => (
                                            <RateLine key={line.mode}>
                                                <RateIcon>{line.icon}</RateIcon>
                                                <RateLabel>{intl.formatMessage({ id: line.labelId })}</RateLabel>
                                                <RateValue
                                                    rateColor={getRateColor(
                                                        rates[line.mode as keyof ConsumptionModeRatesDto],
                                                    )}
                                                >
                                                    {formatRate(rates[line.mode as keyof ConsumptionModeRatesDto])}
                                                </RateValue>
                                            </RateLine>
                                        ))}
                                    </RateCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </StyledTable>
        </TableContainer>
    );
};
